/**
 * GETab — Data-contract export tab for the selected table.
 *
 * Originally surfaced a wall of raw Great-Expectations expectation
 * names (`expect_column_values_to_not_be_null` etc.) which read like
 * library-internal jargon to data stewards and overlapped with the DQ
 * tab. April 2026 reframe: this tab is now positioned as a
 * **data-contract export** for downstream pipelines (CI checks, dbt,
 * Airflow, lakehouse orchestrators). End-users see a clear summary +
 * a one-click download; power users can still expand the full list
 * via the disclosure section at the bottom.
 *
 * Why we keep this tab at all rather than removing it:
 * - The DQ tab covers user-actionable findings (what failed, what to
 *   do). The GE suite covers the orthogonal *machine-actionable*
 *   surface — assertions a CI / scheduled job can re-run continuously
 *   to detect regression. Both are useful; the previous UI just
 *   conflated them.
 */

import { useMemo, useState } from "react";
import {
  ChevronDown,
  ChevronRight,
  Download,
  FileCode,
  Info,
} from "lucide-react";
import type { GEExpectation } from "./DetailPanel";

export interface GETabProps {
  expectations?: GEExpectation[];
  /** Table name — included in the downloaded suite filename. */
  tableName?: string;
}

// Plain-English description per expectation type. Used in the
// disclosure section for power users; the hidden-by-default treatment
// keeps the tab calm for the common case.
const GE_DESCRIPTIONS: Record<string, string> = {
  expect_table_row_count_to_be_between:
    "Row-count bound — table size stays within expected window.",
  expect_table_columns_to_match_ordered_list:
    "Column order & presence — schema matches exactly.",
  expect_table_columns_to_match_set:
    "Column set match — order-agnostic schema check.",
  expect_column_to_exist:
    "Column presence — precondition for column-level checks.",
  expect_column_values_to_not_be_null:
    "Completeness — every row must have a value.",
  expect_column_values_to_be_null:
    "Inverse completeness — every row must be NULL (deprecation signal).",
  expect_column_values_to_be_unique: "Uniqueness — values appear at most once.",
  expect_column_values_to_be_of_type:
    "Type conformance — values match declared SQL type.",
  expect_column_values_to_be_in_type_list:
    "Type-set conformance — values match one of several types.",
  expect_column_values_to_be_in_set: "Allowlist — values fall within an enum.",
  expect_column_values_to_not_be_in_set:
    "Blocklist — values never match a forbidden set.",
  expect_column_values_to_be_between:
    "Value range — numeric / date stays within bounds.",
  expect_column_value_lengths_to_be_between:
    "String-length range — characters per value within bounds.",
  expect_column_value_lengths_to_equal:
    "String-length equality — every value has exactly N chars.",
  expect_column_values_to_match_regex:
    "Pattern — values match a regex (codes, IDs).",
  expect_column_unique_value_count_to_be_between:
    "Cardinality — distinct value count within bounds.",
  expect_column_proportion_of_unique_values_to_be_between:
    "Cardinality ratio — distinct/non-null ratio within bounds.",
  expect_column_most_common_value_to_be_in_set:
    "Mode sanity — most frequent value is expected.",
  expect_column_kl_divergence_to_be_less_than:
    "Distribution drift — KL-divergence below threshold.",
  expect_compound_columns_to_be_unique:
    "Composite uniqueness — tuple uniqueness for composite PKs.",
};

// Family grouping — one row per family in the summary card so users
// can see at a glance "this table has 12 completeness checks, 8
// uniqueness checks, 15 type checks" without reading 78 line items.
const FAMILY: Record<string, string> = {
  expect_table_row_count_to_be_between: "Row count",
  expect_table_columns_to_match_ordered_list: "Schema shape",
  expect_table_columns_to_match_set: "Schema shape",
  expect_column_to_exist: "Schema shape",
  expect_column_values_to_not_be_null: "Completeness",
  expect_column_values_to_be_null: "Completeness",
  expect_column_values_to_be_unique: "Uniqueness",
  expect_compound_columns_to_be_unique: "Uniqueness",
  expect_column_unique_value_count_to_be_between: "Uniqueness",
  expect_column_proportion_of_unique_values_to_be_between: "Uniqueness",
  expect_column_values_to_be_of_type: "Type",
  expect_column_values_to_be_in_type_list: "Type",
  expect_column_values_to_be_in_set: "Allowlist / range",
  expect_column_values_to_not_be_in_set: "Allowlist / range",
  expect_column_values_to_be_between: "Allowlist / range",
  expect_column_value_lengths_to_be_between: "Pattern / format",
  expect_column_value_lengths_to_equal: "Pattern / format",
  expect_column_values_to_match_regex: "Pattern / format",
  expect_column_most_common_value_to_be_in_set: "Distribution",
  expect_column_kl_divergence_to_be_less_than: "Distribution",
};

export function GETab({ expectations = [], tableName = "table" }: GETabProps) {
  const [showAll, setShowAll] = useState(false);

  const families = useMemo(() => {
    const out: Record<string, number> = {};
    for (const e of expectations) {
      const fam = FAMILY[e.expectation_type] || "Other";
      out[fam] = (out[fam] ?? 0) + 1;
    }
    return out;
  }, [expectations]);

  const downloadSuite = () => {
    const suite = {
      data_asset_type: "Dataset",
      expectation_suite_name: `${tableName}_ge_suite`,
      expectations: expectations.map((e) => ({
        expectation_type: e.expectation_type,
        kwargs: e.column ? { column: e.column } : {},
        meta: e.details ? { details: e.details } : {},
      })),
      meta: {
        generated_by: "ZeroTouchData Profiler",
        table: tableName,
        exported_at: new Date().toISOString(),
      },
    };
    const blob = new Blob([JSON.stringify(suite, null, 2)], {
      type: "application/json",
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${tableName.replace(/[^a-z0-9_]/gi, "_")}_ge_suite.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  if (expectations.length === 0) {
    return (
      <div className="space-y-2">
        <p className="text-xs text-white/50">
          No Great Expectations suite generated for this table yet — run a
          profile to populate the data contract.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {/* Why this tab exists — short, plain English */}
      <div className="rounded-md bg-white/[0.03] border border-white/10 px-3 py-2 text-[11px] text-white/65 leading-snug flex items-start gap-2">
        <Info className="w-3.5 h-3.5 text-white/45 shrink-0 mt-0.5" />
        <span>
          A <b>data contract</b> for this table — machine-checkable assertions
          you can run continuously in CI / dbt / Airflow to catch schema and
          quality regressions. The DQ tab tells you what's <i>currently</i>{" "}
          wrong; this is what should <i>stay</i> true going forward. Download
          the JSON suite to wire it into your pipeline.
        </span>
      </div>

      {/* Headline + download */}
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-baseline gap-2">
          <span className="text-2xl font-bold tabular-nums text-emerald-300">
            {expectations.length}
          </span>
          <span className="text-xs text-white/55">
            expectation{expectations.length === 1 ? "" : "s"} cover this table
          </span>
        </div>
        <button
          onClick={downloadSuite}
          className="inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-md bg-emerald-500/15 hover:bg-emerald-500/25 text-emerald-200 border border-emerald-500/30"
          title="Download as a Great Expectations JSON suite"
        >
          <Download className="w-3.5 h-3.5" />
          Download suite
        </button>
      </div>

      {/* Family breakdown — what kinds of checks cover this table */}
      <div className="rounded-md border border-white/10 overflow-hidden">
        <div className="px-3 py-1.5 text-[11px] uppercase tracking-wider text-white/45 bg-white/[0.02] border-b border-white/10">
          Coverage by check family
        </div>
        <ul className="divide-y divide-white/5">
          {Object.entries(families)
            .sort(([, a], [, b]) => b - a)
            .map(([fam, count]) => (
              <li
                key={fam}
                className="flex items-center justify-between px-3 py-1.5 text-[11px]"
              >
                <span className="text-white/80">{fam}</span>
                <span className="font-mono tabular-nums text-white/60">
                  {count}
                </span>
              </li>
            ))}
        </ul>
      </div>

      {/* Power-user disclosure: full expectation list, hidden by default */}
      <div className="rounded-md border border-white/10 overflow-hidden">
        <button
          onClick={() => setShowAll((v) => !v)}
          className="w-full flex items-center gap-2 px-3 py-1.5 text-[11px] text-white/65 hover:text-white/85 hover:bg-white/[0.03]"
          aria-expanded={showAll}
        >
          {showAll ? (
            <ChevronDown className="w-3.5 h-3.5" />
          ) : (
            <ChevronRight className="w-3.5 h-3.5" />
          )}
          <FileCode className="w-3.5 h-3.5" />
          {showAll ? "Hide" : "Show"} all {expectations.length} expectations
        </button>
        {showAll && (
          <ul className="divide-y divide-white/5 max-h-[280px] overflow-y-auto">
            {expectations.map((e, i) => (
              <li key={i} className="px-3 py-1.5 text-[11px]">
                <div className="font-mono text-white/85 break-all">
                  {e.expectation_type}
                  {e.column && (
                    <span className="text-white/50"> · {e.column}</span>
                  )}
                </div>
                <div className="text-white/50 mt-0.5">
                  {GE_DESCRIPTIONS[e.expectation_type] ||
                    "Great Expectations rule."}
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
